<script lang="ts">
    import { goto } from "$app/navigation";
    import { page } from "$app/stores";
    import Footer from "$lib/components/navigation/footer.svelte";
    import Navbar from "$lib/components/navigation/navbar.svelte";
    import ErrorPage from "$lib/components/section/ErrorPage.svelte";
    import NewsDetailsPagesCmu from "$lib/components/section/News/catchmeup/NewsDetailsPagesCMU.svelte";
    import NewsDetailsPages from "$lib/components/section/News/common/NewsDetailsPages.svelte";
    import { MUNIO } from "$lib/munio";
    import { MUNIO_V2 } from "$lib/munioV2";
    import { getAuthToken } from "$lib/stores/auth";
    import { isPageActive } from "$lib/utils/is-page-active";
    import { setCookie } from "$lib/utils/setCookie";
    import { ProgressBar } from "@prgm/sveltekit-progress-bar";
    import "@splidejs/svelte-splide/css/core";
    import "dayjs/locale/id";
    import Cookies from "js-cookie";
    import { onMount } from "svelte";
    import { dayjs } from "svelte-time";
    import type { PageData } from "./$types";

    export let data: PageData | any;
    $: news = data?.news;
    $: detail = data?.detail;
    $: categories = data?.categories;
    let appreanceConfig = data?.appreanceConfig?.data;

    let client = data?.client;
    let permissions = data?.permissions;
    let customPath = data?.customPath;
    $: detailViewed = data?.detail?.viewed_counts;
    $: detailShared = data?.detail?.shared_counts;

    let formShareSocialMedia = false;
    let isCopyToClipboard: boolean = false;

    const primaryColor = appreanceConfig?.colors?.primary;
    const secondaryColor = appreanceConfig?.colors?.secondary;
    const currentPage = isPageActive("news", client, permissions);

    const handleCopyLinkToClipboard = () => {
        const urlToCopy = window.location.href;

        navigator.clipboard
            .writeText(urlToCopy)
            .then(() => {
                isCopyToClipboard = true;

                setTimeout(() => {
                    isCopyToClipboard = false;
                }, 3000);
            })
            .catch((err) => {
                console.error("Failed to copy URL to clipboard", err);
            });
    };

    const handleAPICountViewed = async () => {
        const cookie = Cookies.get("idf");
        if (!cookie) {
            let type = "news";
            let slug = data?.detail?.slug;

            await MUNIO.initMunio(await MUNIO.getClientKey($page.url.origin));
            await MUNIO_V2.initMunio(
                await MUNIO_V2.getClientKey($page.url.origin),
            );
            let response = await MUNIO.getIdentifierActivities();

            setCookie("idf", response?.data?.identifier, "/");
            let identifier = response?.data?.identifier;

            if (response) {
                let response_count = await MUNIO.countView({
                    type,
                    slug,
                    identifier,
                });
                if (response_count) {
                    let response_detail = await MUNIO_V2.getPostsDetailV2(
                        data?.detail?.slug,
                    );
                    detailViewed = response_detail?.viewed_counts;
                    detailShared = response_detail?.shared_counts;
                }
            }
        } else {
            let type = "news";
            let slug = data?.detail?.slug;
            let identifier = cookie;

            await MUNIO.initMunio(await MUNIO.getClientKey($page.url.origin));
            await MUNIO_V2.initMunio(
                await MUNIO_V2.getClientKey($page.url.origin),
            );
            let response = await MUNIO.countView({
                type,
                slug,
                identifier,
            });
            if (response) {
                let response_detail = await MUNIO_V2.getPostsDetailV2(
                    data?.detail?.slug,
                );
                detailViewed = response_detail?.viewed_counts;
                detailShared = response_detail?.shared_counts;
            }
        }
    };

    const handleAPICountShared = async () => {
        const cookie = Cookies.get("idf");
        if (!cookie) {
            let type = "news";
            let slug = data?.detail?.slug;

            await MUNIO.initMunio(await MUNIO.getClientKey($page.url.origin));
            await MUNIO_V2.initMunio(
                await MUNIO_V2.getClientKey($page.url.origin),
            );
            let response = await MUNIO.getIdentifierActivities();

            setCookie("idf", response?.data?.identifier, "/");
            let identifier = response?.data?.identifier;

            if (response) {
                let response_count = await MUNIO.countShare({
                    type,
                    slug,
                    identifier,
                });
                if (response_count) {
                    let response_detail = await MUNIO_V2.getPostsDetailV2(
                        data?.detail?.slug,
                    );
                    detailShared = response_detail?.shared_counts;
                }
            }
        } else {
            let type = "news";
            let slug = data?.detail?.slug;
            let identifier = cookie;

            await MUNIO.initMunio(await MUNIO.getClientKey($page.url.origin));
            await MUNIO_V2.initMunio(
                await MUNIO_V2.getClientKey($page.url.origin),
            );
            let response = await MUNIO.countShare({
                type,
                slug,
                identifier,
            });
            if (response) {
                let response_detail = await MUNIO_V2.getPostsDetailV2(
                    data?.detail?.slug,
                );
                detailShared = response_detail?.shared_counts;
            }
        }
    };

    const handleFromShareSocialMedia = (post: any) => {
        formShareSocialMedia = !formShareSocialMedia;
    };

    onMount(async () => {
        let authToken = await getAuthToken();
        if (!authToken && data?.client?.force_user_to_login === 1) {
            goto("/login");
        }
        handleAPICountViewed();
    });
</script>

<svelte:head>
    <title>{detail?.title} - {client?.name}</title>
    <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={`${client?.favicon === null ? "/favicon-32x32.png" : client?.favicon}`}
    />
    <link
        rel="icon"
        type="image/png"
        sizes="96x96"
        href={`${client?.favicon === null ? "/favicon-96x96.png" : client?.favicon}`}
    />
    <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={`${client?.favicon === null ? "/favicon-16x16.png" : client?.favicon}`}
    />
</svelte:head>

<Navbar data={client} dataPermission={permissions} {customPath} />
<ProgressBar color={primaryColor} class="text-[${primaryColor}]" zIndex={100} />
{#if detail}
    {#if client?.code === "catchmeup"}
        <NewsDetailsPagesCmu
            {detail}
            {primaryColor}
            {client}
            {currentPage}
            {detailShared}
            {detailViewed}
            {formShareSocialMedia}
            {categories}
            {news}
            {handleFromShareSocialMedia}
        />
    {:else}
        <NewsDetailsPages
            {detail}
            {primaryColor}
            {client}
            {currentPage}
            {detailShared}
            {detailViewed}
            {formShareSocialMedia}
            {categories}
            {news}
            {handleFromShareSocialMedia}
        />
    {/if}

    {#if formShareSocialMedia}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
            class={`backdrop ${formShareSocialMedia ? "open-backdrop" : "close-backdrop"} w-full h-full overflow-y-auto py-[0] sm:px-0 px-4 fixed z-[9999] top-0 bg-black bg-opacity-50 flex justify-center items-center`}
        >
            <div
                class={`dialog ${formShareSocialMedia ? "open" : "close"} w-[30rem] h-auto overflow-auto bg-neutral-50 rounded-[10px]`}
                on:click={(event) => event?.stopPropagation()}
            >
                <div
                    class="header-form px-[15px] border-b border-zinc-950 border-opacity-5 flex items-center justify-between py-[20px]"
                >
                    <div class="text-[1.35rem] text-slate-900">
                        Bagikan {currentPage}
                    </div>
                    <svg
                        on:click={() => {
                            formShareSocialMedia = false;
                            document.body.style.overflow = "auto";
                        }}
                        class="cursor-pointer hover:text-slate-900 text-slate-700"
                        xmlns="http://www.w3.org/2000/svg"
                        width="30"
                        height="30"
                        viewBox="0 0 24 24"
                        ><path
                            fill="currentColor"
                            d="m12 10.587l4.95-4.95l1.414 1.414l-4.95 4.95l4.95 4.95l-1.415 1.414l-4.95-4.95l-4.949 4.95l-1.414-1.415l4.95-4.95l-4.95-4.95L7.05 5.638z"
                        /></svg
                    >
                </div>
                <div class="detail-form py-[25px] px-[15px]">
                    <div class="wrapper-share-content space-y-[23px]">
                        <div class="content-highlight flex gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                ><path
                                    fill="currentColor"
                                    d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10s-4.477 10-10 10m0-2a8 8 0 1 0 0-16a8 8 0 0 0 0 16M11 7h2v2h-2zm0 4h2v6h-2z"
                                /></svg
                            >
                            <div class="space-y-1">
                                <span class="text-slate-500"
                                    >Judul {currentPage}</span
                                >
                                <h4
                                    class="text-slate-800 leading-normal text-[1.25rem]"
                                >
                                    {detail?.title}
                                </h4>
                            </div>
                        </div>
                        <div class="content-button-share flex gap-3">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                ><path
                                    fill="currentColor"
                                    d="m13.12 17.023l-4.199-2.29a4 4 0 1 1 0-5.465l4.2-2.29a4 4 0 1 1 .958 1.755l-4.2 2.29a4.008 4.008 0 0 1 0 1.954l4.2 2.29a4 4 0 1 1-.959 1.755M6 14a2 2 0 1 0 0-4a2 2 0 0 0 0 4m11-6a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 12a2 2 0 1 0 0-4a2 2 0 0 0 0 4"
                                /></svg
                            >
                            <div class="space-y-2">
                                <span class="text-slate-500">Bagikan Via</span>
                                <div class="flex flex-wrap items-center gap-3">
                                    {#if isCopyToClipboard}
                                        <button
                                            type="button"
                                            class="copy-to-clipboard pointer-events-none flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="30"
                                                height="30"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    fill="currentColor"
                                                    d="m10 15.17l9.192-9.191l1.414 1.414L10 17.999l-6.364-6.364l1.414-1.414z"
                                                /></svg
                                            >
                                            <span
                                                class="text-slate-600 group-hover:text-slate-700 text-sm"
                                                >Tautan Disalin</span
                                            >
                                        </button>
                                    {:else}
                                        <button
                                            type="button"
                                            on:click={() => {
                                                handleAPICountShared();
                                                handleCopyLinkToClipboard();
                                            }}
                                            class="copy-to-clipboard flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="30"
                                                height="30"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    fill="currentColor"
                                                    d="M18.364 15.536L16.95 14.12l1.414-1.414a5 5 0 0 0-7.071-7.071L9.878 7.05L8.464 5.636l1.414-1.414a7 7 0 0 1 9.9 9.9zm-2.829 2.828l-1.414 1.414a7 7 0 0 1-9.9-9.9l1.415-1.414L7.05 9.88l-1.414 1.414a5 5 0 0 0 7.07 7.071l1.415-1.414zm-.707-10.607l1.415 1.415l-7.072 7.07l-1.414-1.414z"
                                                /></svg
                                            >
                                            <span
                                                class="text-slate-600 group-hover:text-slate-700 text-sm"
                                                >Salin Tautan</span
                                            >
                                        </button>
                                    {/if}
                                    <!-- svelte-ignore a11y-invalid-attribute -->
                                    <div
                                        data-href="https://developers.facebook.com/docs/plugins/"
                                        data-layout="button"
                                        data-size="small"
                                    >
                                        <a
                                            on:click={() => {
                                                handleAPICountShared();
                                                window.open(
                                                    `https://www.facebook.com/sharer/sharer.php?u=https://${client?.domain === "" ? `${client?.subdomain}.munio.id/${detail?.slug}` : `${client?.domain}/${detail?.slug}`}&title=${detail?.title}&quote=&hashtag=`,
                                                    "",
                                                    "popup",
                                                );
                                            }}
                                            href="javascript:void(0);"
                                            class="fb-xfbml-parse-ignore share-facebook flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer no-underline hover:no-underline"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                class="text-center text-[#1877F2]"
                                                width="30"
                                                height="30"
                                                viewBox="0 0 24 24"
                                                ><path
                                                    fill="currentColor"
                                                    d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202z"
                                                /></svg
                                            >
                                            <span
                                                class="text-slate-600 group-hover:text-slate-700 text-sm"
                                                >Facebook</span
                                            >
                                        </a>
                                    </div>
                                    <!-- svelte-ignore a11y-invalid-attribute -->
                                    <a
                                        on:click={() => {
                                            handleAPICountShared();
                                            window.open(
                                                `https://twitter.com/intent/tweet?text=${detail?.title} - ${window.location.href}`,
                                                "",
                                                "popup",
                                            );
                                        }}
                                        href="javascript:void(0);"
                                        class="share-twitter flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer no-underline hover:no-underline"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="text-center text-[#1877F2]"
                                            width="30"
                                            height="30"
                                            viewBox="0 0 128 128"
                                            ><path
                                                d="M75.916 54.2L122.542 0h-11.05L71.008 47.06L38.672 0H1.376l48.898 71.164L1.376 128h11.05L55.18 78.303L89.328 128h37.296L75.913 54.2ZM60.782 71.79l-4.955-7.086l-39.42-56.386h16.972L65.19 53.824l4.954 7.086l41.353 59.15h-16.97L60.782 71.793Z"
                                            /></svg
                                        >
                                        <span
                                            class="text-slate-600 group-hover:text-slate-700 text-sm"
                                            >Twitter</span
                                        >
                                    </a>
                                    <!-- svelte-ignore a11y-invalid-attribute -->
                                    <a
                                        on:click={() => {
                                            handleAPICountShared();
                                            window.open(
                                                `https://api.whatsapp.com/send?text=${detail?.title} - ${window.location.href}`,
                                                "",
                                                "popup",
                                            );
                                        }}
                                        href="javascript:void(0);"
                                        class="share-whatsapp flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer no-underline hover:no-underline"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="text-center"
                                            width="30"
                                            height="30"
                                            viewBox="0 0 256 258"
                                            ><path
                                                fill="#25D366"
                                                d="M128.534 0c34.098.017 66.102 13.29 90.167 37.383c24.066 24.092 37.312 56.12 37.299 90.174c-.028 69.579-56.076 126.318-125.36 127.446l-2.103.017h-.053c-21.005-.007-41.65-5.194-60.051-15.045l-.86-.466L0 257.233l18.083-66.055C6.93 171.852 1.061 149.922 1.07 127.455C1.098 57.178 58.279 0 128.534 0m.044 21.53c-58.437 0-105.964 47.523-105.987 105.936c-.008 19.712 5.424 38.921 15.719 55.612l.478.769l2.52 4.009l-10.703 39.093l40.097-10.517l3.869 2.294c16.007 9.499 34.32 14.599 53.017 14.764l.905.004h.044c58.392 0 105.918-47.526 105.942-105.943c.01-28.308-10.998-54.927-31.001-74.952c-20.003-20.024-46.603-31.06-74.9-31.07m-45.17 47.063c2.122 0 4.25.02 6.104.115c1.956.096 4.581-.743 7.165 5.466c.992 2.38 2.5 6.057 4.044 9.813l.331.805c2.485 6.045 4.969 12.072 5.447 13.029c.795 1.593 1.325 3.455.264 5.579c-1.06 2.127-1.593 3.454-3.184 5.316c-1.593 1.858-3.344 4.153-4.777 5.579a57.05 57.05 0 0 0-.234.233l-.232.237c-1.42 1.469-2.608 3.153-.93 6.027c1.857 3.19 8.248 13.622 17.716 22.066c12.165 10.85 22.428 14.214 25.613 15.809c3.184 1.594 5.043 1.328 6.9-.8c1.859-2.124 7.961-9.298 10.085-12.487c2.123-3.188 4.246-2.655 7.166-1.593c1.387.505 5.654 2.511 10.092 4.649l1.38.666c4.443 2.15 8.776 4.288 10.29 5.046c3.185 1.595 5.309 2.392 6.104 3.719c.796 1.33.796 7.705-1.858 15.145c-2.653 7.44-15.378 14.23-21.497 15.146c-5.487.818-12.43 1.16-20.06-1.262c-4.624-1.47-10.558-3.429-18.157-6.71c-31.948-13.794-52.815-45.966-54.406-48.09l-.108-.144c-1.941-2.594-12.898-17.445-12.898-32.804c0-15.675 8.229-23.38 11.147-26.569c2.92-3.186 6.37-3.986 8.493-3.986"
                                            /></svg
                                        >
                                        <span
                                            class="text-slate-600 group-hover:text-slate-700 text-sm"
                                            >Whatsapp</span
                                        >
                                    </a>
                                    <!-- svelte-ignore a11y-invalid-attribute -->
                                    <a
                                        on:click={() => {
                                            handleAPICountShared();
                                            window.open(
                                                `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&summary=${detail?.title} - ${window.location.href}`,
                                                "",
                                                "popup",
                                            );
                                        }}
                                        href="javascript:void(0);"
                                        class="share-linkedin flex flex-col justify-center items-center group hover:bg-gray-200/50 transition-colors duration-200 rounded-[8px] p-2 space-y-2 cursor-pointer no-underline hover:no-underline"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="30"
                                            height="30"
                                            viewBox="0 0 128 128"
                                            ><path
                                                fill="#0076b2"
                                                d="M116 3H12a8.91 8.91 0 0 0-9 8.8v104.42a8.91 8.91 0 0 0 9 8.78h104a8.93 8.93 0 0 0 9-8.81V11.77A8.93 8.93 0 0 0 116 3"
                                            /><path
                                                fill="#fff"
                                                d="M21.06 48.73h18.11V107H21.06zm9.06-29a10.5 10.5 0 1 1-10.5 10.49a10.5 10.5 0 0 1 10.5-10.49m20.41 29h17.36v8h.24c2.42-4.58 8.32-9.41 17.13-9.41C103.6 47.28 107 59.35 107 75v32H88.89V78.65c0-6.75-.12-15.44-9.41-15.44s-10.87 7.36-10.87 15V107H50.53z"
                                            /></svg
                                        >
                                        <span
                                            class="text-slate-600 group-hover:text-slate-700 text-sm"
                                            >Linkedin</span
                                        >
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- <div class="content-footer bg-gray-200/50 py-4 flex justify-center">
              <div style="background-color: {primaryColor};" class={`close-share-button px-4 py-2 font-medium rounded-[8px] mx-auto text-center text-white transition-opacity duration-200 cursor-pointer`} on:click={() => {
                formShareSocialMedia = false;
                document.body.style.overflow = 'auto'
              }}>
                Tutup
              </div>
            </div> -->
            </div>
        </div>
    {/if}
{:else}
    <ErrorPage {client} />
{/if}
<Footer data={client} />

<style lang="css">
    /* .splide__arrows {
        display: none !important;
    }
  .splide__pagination {
      display: none !important;
  } */

    .splide__arrows > .splide__arrow:nth-child(2) {
        top: 50%;
        right: 30px;
        opacity: 50%;
        background: white;
        transform: translateY(-50%);
    }
    .splide__arrows > .splide__arrow:nth-child(2):hover {
        opacity: 100%;
    }
    .splide__arrows > .splide__arrow:nth-child(1) {
        top: 50%;
        left: 30px;
        opacity: 50%;
        background: white;
        transform: translateY(-50%);
    }
    .splide__arrows > .splide__arrow:nth-child(1):hover {
        opacity: 100%;
    }
    .close-share-button:hover {
        opacity: 0.8;
    }
    :global(.tooltip.custom-tooltip) {
        --tooltip-box-shadow: 0 !important;
        --tooltip-color: white !important;
        font-family: Helvetica, Arial, sans-serif;
    }
    .categories-item:hover {
        opacity: 1 !important;
    }
    .dialog.open {
        animation: zoom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }
    @keyframes zoom {
        from {
            transform: scale(0.3);
        }
        to {
            transform: scale(1);
        }
    }
    .backdrop.open-backdrop {
        animation: fade 0.3s ease-out;
    }
    @keyframes fade {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
    @media (min-width: 768px) {
        :global(.svelte-lightbox-thumbnail) {
            height: 300px;
        }
    }
    @media (min-width: 1024px) {
        :global(.svelte-lightbox-thumbnail) {
            height: 350px;
        }
    }
    @media (min-width: 1536px) {
        :global(.svelte-lightbox-thumbnail) {
            height: 700px;
        }
    }
    @media only screen and (max-width: 991px) {
        .sidebar__wrapper {
            padding: 30px 0 !important;
        }
    }
    @media only screen and (max-width: 435px) {
        .container-information-content {
            flex-direction: column;
            align-items: start;
        }
        .wrapper-viewed-shared {
            width: 100%;
            justify-content: space-between;
        }
        .wrapper-content-share,
        .share-button {
            width: 100%;
        }
        .share-button {
            justify-content: center;
        }
    }
</style>
